import { useState, useEffect } from 'react';
import { Customer } from '../../Customer/Model/CustomerModel';
import { CheckBoxItem } from '../../../Common/CommonModel';
import { globalService } from '../../UIContainer/Global/GlobalService';
import { farmService } from '../../Farm/FarmService';

interface UseCustomerIdsProps {
  filterOption: string;
  customers: Customer[];
  customerOptions: CheckBoxItem[];
  farmOptions: CheckBoxItem[];
  tagOptions: CheckBoxItem[];
  setLoading: (loading: boolean) => void;
  setError: (error: string) => void;
  setFilteredCustomers: (customers: Customer[]) => void;
  setCustomer: (customer: Customer) => void;
}

export const useCustomerIds = ({
  filterOption,
  customers,
  customerOptions,
  farmOptions,
  tagOptions,
  setLoading,
  setError,
  setFilteredCustomers,
  setCustomer,
}: UseCustomerIdsProps) => {
  const [customerIds, setCustomerIds] = useState<string[]>([]);

  useEffect(() => {
    loadCustomerIds();
  }, [filterOption, farmOptions, customerOptions, tagOptions]);

  const loadCustomerIds = async (): Promise<void> => {
    setCustomerIds([]);

    let customerIds: string[] = [];

    switch (filterOption) {
      case 'customer':
        customerIds = getCustomerIds();
        break;

      case 'farm':
        customerIds = await getFarmCustomerIds();
        break;

      case 'tags':
        customerIds = await getTagCustomerIds();
        break;

      case 'allcustomers':
        customerIds = getAllCustomerIds();
        break;

      default:
        break;
    }

    setCustomerIds(customerIds);
  };

  const getCustomerIds = (): string[] => {
    setLoading(true);
    try {
      const filteredCustomersList = customers.filter((customer) =>
        customerOptions.some(
          (option) => option.Value === customer.Id && option.Check,
        ),
      );
      setFilteredCustomers(filteredCustomersList);
      setCustomer(filteredCustomersList[0]);
      return customerOptions
        .filter((customer) => customer.Check)
        .map((customer) => customer.Value);
    } finally {
      setLoading(false);
    }
  };

  const getFarmCustomerIds = async (): Promise<string[]> => {
    setLoading(true);
    try {
      const farmPromises = farmOptions
        .filter((farm) => farm.Check)
        .map((farm) => getTenantIdsOnFarm(Number(farm.Value)));

      const resolvedFarmIds = await Promise.all(farmPromises);
      const farmCustomerIds = resolvedFarmIds.flat();
      setFilteredCustomers(
        customers.filter((customer) => farmCustomerIds.includes(customer.Id)),
      );
      const matchedCustomer = customers.find(
        (customer) => customer.Id === farmCustomerIds[0],
      );
      if (matchedCustomer) {
        setCustomer(matchedCustomer);
      }
      return farmCustomerIds;
    } catch (error) {
      console.error('Error fetching farm customer IDs:', error);
      setError('Failed to fetch farm customer IDs. Please try again.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getTenantIdsOnFarm = async (farmId: number): Promise<string[]> => {
    setLoading(true);
    try {
      const tenants = await farmService.getTenantsOnFarm(farmId);
      return tenants.Items.map((tenant) => tenant.Id);
    } catch (error) {
      console.error(`Error fetching tenants for farm ID ${farmId}:`, error);
      setError('Failed to fetch tenants. Please try again.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getTagCustomerIds = async (): Promise<string[]> => {
    setLoading(true);
    try {
      const tagPromises = tagOptions
        .filter((tag) => tag.Check)
        .map((tag) => getCustomerIdsByTag(tag.Value));

      const resolvedTagIds = await Promise.all(tagPromises);
      const tagCustomerIds = resolvedTagIds.flat();
      setFilteredCustomers(
        customers.filter((customer) => tagCustomerIds.includes(customer.Id)),
      );
      const matchedCustomer = customers.find(
        (customer) => customer.Id === tagCustomerIds[0],
      );
      if (matchedCustomer) {
        setCustomer(matchedCustomer);
      }
      return tagCustomerIds;
    } catch (error) {
      console.error('Error fetching tag customer IDs:', error);
      setError('Failed to fetch tag customer IDs. Please try again.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getCustomerIdsByTag = async (tagName: string): Promise<string[]> => {
    setLoading(true);
    try {
      const tags = await globalService.getTagDetailsByObjectType('customer');
      return tags.Items.filter(
        (tagDetail) => tagDetail.TagName === tagName,
      ).map((tagDetail) => tagDetail.ObjectId);
    } catch (error) {
      console.error('Error fetching customer IDs by tag:', error);
      setError('Failed to fetch customer IDs by tag. Please try again.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getAllCustomerIds = (): string[] => {
    setLoading(true);
    try {
      setFilteredCustomers(customers);
      setCustomer(customers[0]);
      return customerOptions.map((customer) => customer.Value);
    } finally {
      setLoading(false);
    }
  };

  return customerIds;
};
